import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AdminMenu from "../../AdminMenu";

import {
  GoogleMap,
  useLoadScript,
  Polygon,
  Autocomplete,
  OverlayView,
} from "@react-google-maps/api";

import { getUserData } from "../../../utils/localStorage";
import { checkStorePolygon } from "../../../services/StoreUsers/PostStoreUsers";

const DEFAULT_CENTER = { lat: 28.6139, lng: 77.209 };
const LIBRARIES = ["marker", "places"]; // static, prevents reload warning

const GeoLocationAllStoreViewMap = ({resAPIKey}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { StoreData } = location.state || {}; // Fallback to prevent undefined errors

  const { brandProfileData } = getUserData();
  let brandId = brandProfileData.id;

  const [congestionPolygon, setCongestionPolygon] = useState([]);

  const autocompleteRef = useRef(null);

  const [visible, setVisible] = useState(false);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: resAPIKey,
    libraries: LIBRARIES,
  });

  const [userLocation, setUserLocation] = useState(DEFAULT_CENTER);
  const [points, setPoints] = useState([]); // points while drawing  

  const mapRef = useRef(null);

  const onPlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();

    if (!place.geometry) return;

    const location = place.geometry.location;

    const newCenter = {
      lat: location.lat(),
      lng: location.lng(),
    };

    setUserLocation(newCenter);

    // smooth pan
    if (mapRef.current) {
      mapRef.current.panTo(newCenter);
    }
  };

  const fetchStorePolygon = () => {
    checkStorePolygon(brandId)
      .then((response) => {
        if (!response.data.error) {
          const Res = response.data;
          setCongestionPolygon(Res)
        }
      })
      .catch((err) => {
        console.error("Error:", err);
      });
  };

  // Get user's current location
  useEffect(() => {
    fetchStorePolygon();

    if (navigator.geolocation) {
      if (StoreData?.address?.latitude) {
        setUserLocation({
          lat: StoreData?.address?.latitude,
          lng: StoreData?.address?.longitude,
        });
      } else {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setUserLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
          },
          (error) => {
            console.error("Error getting location:", error);
            setUserLocation(DEFAULT_CENTER);
          },
        );
      }
    }

  }, []);

  // Handle clicks to add points
  const handleMapClick = (e) => {
    setPoints((prev) => [
      ...prev,
      { lat: e.latLng.lat(), lng: e.latLng.lng() },
    ]);
  };

  // Clear map reference when unmounted
  const onUnmount = () => {
    mapRef.current = null;
  };

  if (!isLoaded) return <div>Loading map...</div>;

  return (
    <div className="flex flex-column h-full">
      {/* Header */}
      <div
        className="h-3rem surface-0 sticky z-2 top-0 flex justify-content-center align-items-center"
        style={{ "--surface-0": "#1752AA" }}
      >
        <div className="flex-1 flex ml-3 mr-3">
          <div className="flex-1 flex">
            <div className="ml-3 flex justify-content-center align-items-center">
              <div className="h-1rem w-1rem border-circle flex justify-content-center align-items-center">
                <i
                  className="pi pi-angle-left"
                  onClick={() => navigate("/StoreManagementList")}
                  style={{ fontSize: "27px", cursor: "pointer", color: "#fff" }}
                ></i>
              </div>
            </div>
            <div className="flex-1 ml-5">
              <p className="text-2xl text-white font-bold uppercase">
              All Store Geo Location
              </p>
            </div>
          </div>
          <div
            className="flex justify-content-center align-items-center cursor-pointer"
            onClick={() => setVisible(true)}
          >
            <i
              className="pi pi-bars"
              style={{ fontSize: "27px", marginRight: 30, color: "#fff" }}
            ></i>
          </div>
        </div>
      </div>

      {/* Main Layout */}
       
       {congestionPolygon.length > 0 ?
        <div className="flex flex-1 p-4 gap-4">
          {/* MAP SECTION */}
          {/* 🗺️ MAP CARD */}
          <div
            className="flex-1 border-round-xl shadow-3 overflow-hidden"
            style={{
              background: "#fff",
              position: "relative",
            }}
          >
            <div
              style={{ position: "absolute", top: 10, left: 10, zIndex: 10 }}
            >
              <Autocomplete
                onLoad={(ref) => (autocompleteRef.current = ref)}
                onPlaceChanged={onPlaceChanged}
              >
                <input
                  type="text"
                  placeholder="Search location..."
                  style={{
                    width: "260px",
                    height: "40px",
                    padding: "0 12px",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                    outline: "none",
                  }}
                />
              </Autocomplete>
            </div>

            <GoogleMap
              zoom={12}
              center={userLocation}
              mapContainerStyle={{ height: "450px", width: "100%" }}
              onClick={handleMapClick}
              onLoad={(map) => (mapRef.current = map)}
              onUnmount={onUnmount}
            >

              {congestionPolygon.map((cp, i) => (
                <React.Fragment key={cp?.id}>
                  <Polygon
                    // Deliberately NOT `path={polygon}` — see the note above
                    // syncPolygonPath for why that breaks multi-point edits.
                    path={cp?.coordinates?.map((point) => ({
                      lat: point?.[1],
                      lng: point?.[0],
                    }))}
                    options={{
                      // draggable: true, // Allows the user to click and drag the whole shape
                      // editable: true, // Allows dragging individual vertices AND dragging an
                      // edge midpoint out to create a brand new vertex there
                      fillColor: "#f32121",
                      fillOpacity: 0.25,
                      strokeColor: "#a10d0d",
                      strokeWeight: 2,
                    }}
                  />

                  {/* Render the Text Label at the Center */}
                  <OverlayView
                    position={{
                      lat: cp?.storeInfo?.address?.latitude,
                      lng: cp?.storeInfo?.address?.longitude,
                    }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <div
                      style={{
                        background: "white",
                        color: "#da13d0",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                        fontSize: "14px",
                        fontWeight: "bold",
                        transform: "translate(-50%, -50%)", // Centers the text block perfectly over the coordinate
                        whiteSpace: "nowrap",
                      }}
                    >
                      {cp?.storeInfo?.storeName +
                        " | " +
                        cp?.storeInfo?.storeCode}
                    </div>
                  </OverlayView>
                </React.Fragment>
              ))}
            </GoogleMap>
          </div>
        </div>
        : <div className="flex justify-content-center align-items-center mt-4">
                        <p className="text-3xl font-semi-bold">Please Enter Store Location</p>
                    </div>
                    }
       

      <AdminMenu setVisible={setVisible} visible={visible} />
    </div>
  );
};

export default GeoLocationAllStoreViewMap;
